<script setup>
import DefaultLayout from "@/components/layouts/DefaultLayout.vue";
import { computed, ref } from "vue";
import { gql } from "@apollo/client/core";
import { useMutation, useQuery, useResult } from "@vue/apollo-composable";
import { useAuthStore } from "@/stores/auth";
import { ClientError, getMessageFromError } from "@/libs/errors";
import { dateToUserWithTime, formatCurrency } from "@/libs/filters";
import UserArea from "../../../components/ui/UserArea.vue";
import FormGroup from "../../../components/form/FormGroup.vue";
import { platform } from "@/libs/environment";
import { useCordovaStore } from "@/stores/cordova";
import NewTrial from "./NewTrial.vue";

const cordovaStore = useCordovaStore();
const authStore = useAuthStore();

const invoiceUrl = ref("");

function onTrialActivated(url) {
  invoiceUrl.value = url;
}

const plan = ref("MONTHLY");
const billingType = ref("CREDIT_CARD");
const yearlyValue = 4990;
const monthlyValue = 97;
const installmentCountMax = 12;
const installmentCount = ref(installmentCountMax);
const installmentOptions = {};
Array.from({ length: installmentCountMax }, (e, i) => {
  installmentOptions[i + 1] = `${i + 1}x ${formatCurrency(
    yearlyValue / (i + 1)
  )} = ${formatCurrency(yearlyValue)} sem juros`;
});

const kindOfPerson = ref("NATURAL_PERSON");
const legalName = ref();
const regNumber = ref();

const addressLine1 = ref();
const addressNumber = ref();
const addressLine2 = ref();
const addressLine3 = ref();
const city = ref();
const state = ref();
const postalCode = ref();

const name = ref();
const email = ref();
const phone = ref();
const errorMessage = ref("");
const saving = ref(false);

const {
  loading: userLoading,
  error: userError,
  result: userResult,
  onResult: userOnResult,
} = useQuery(gql`
  query Me {
    me {
      id
      name
      email
      phone
      state
      kindOfPerson
      legalName
      regNumber
      addressLine1
      addressNumber
      addressLine2
      addressLine3
      city
      postalCode
    }
  }
`);
const user = useResult(userResult, null, (data) => data.me);

const {
  loading: subscriptionsLoading,
  error: subscriptionsError,
  result: subscriptionsResult,
} = useQuery(
  gql`
    query MySubscriptions {
      mySubscriptions {
        id
        description
        createdAt
        validFrom
        validUntil
        cycle
        nextDueDate
        billingType
        status
        active
        trial
        value
      }
    }
  `,
  {},
  {
    fetchPolicy: "no-cache",
  }
);
const subscriptions = useResult(
  subscriptionsResult,
  null,
  (data) => data.mySubscriptions
);

const trialElegible = computed(() => {
  // Usuário pode ser elegível para trial se não houver nenhuma assinatura ativa nem nenhuma trial anterior.
  if (!subscriptions.value || !user.value) return false;
  // Não tem assinatura ativa
  const hasActive = !!subscriptions.value.find((s) => s.active);
  // Já teve trial antes
  const hadTrial = !!subscriptions.value.find((s) => s.trial);
  return !hasActive && !hadTrial;
});

const setRefs = () => {
  if (!user.value) {
    return;
  }
  name.value = user.value.name;
  state.value = user.value.state;
  email.value = user.value.email;
  phone.value = user.value.phone;
  kindOfPerson.value = user.value.kindOfPerson || "NATURAL_PERSON";
  legalName.value = user.value.legalName;
  regNumber.value = user.value.regNumber;
  addressLine1.value = user.value.addressLine1;
  addressNumber.value = user.value.addressNumber;
  addressLine2.value = user.value.addressLine2;
  addressLine3.value = user.value.addressLine3;
  state.value = user.value.state;
  city.value = user.value.city;
  postalCode.value = user.value.postalCode;
};
setRefs();
userOnResult(setRefs);

const { mutate: updateMyFiscalInfo } = useMutation(gql`
  mutation UpdateMyFiscalInfo($input: UpdateFiscalInfoInput!) {
    updateMyFiscalInfo(input: $input) {
      id
      kindOfPerson
      legalName
      regNumber
      postalCode
      addressLine1
      addressNumber
      addressLine2
      addressLine3
      city
      state
    }
  }
`);

const { mutate: subscribeViaWeb } = useMutation(gql`
  mutation SubscribeViaWeb(
    $plan: Plan!
    $billingType: BillingType!
    $installmentCount: Float
  ) {
    subscribeViaWeb(
      plan: $plan
      billingType: $billingType
      installmentCount: $installmentCount
    )
  }
`);

const submit = async () => {
  if (saving.value) {
    return;
  }
  saving.value = true;
  errorMessage.value = "";
  try {
    await updateMyFiscalInfo({
      input: {
        kindOfPerson: kindOfPerson.value,
        legalName: legalName.value,
        regNumber: regNumber.value,
        postalCode: postalCode.value,
        addressLine1: addressLine1.value,
        addressNumber: addressNumber.value,
        addressLine2: addressLine2.value,
        addressLine3: addressLine3.value,
        city: city.value,
        state: state.value,
      },
    });
    authStore.setState(state.value);
    const subscribeResponse = await subscribeViaWeb({
      plan: plan.value,
      billingType: billingType.value,
      installmentCount: installmentCount.value,
    });
    if (!subscribeResponse) {
      throw new ClientError("Verifique o seu e-mail");
    }
    invoiceUrl.value = subscribeResponse.data.subscribeViaWeb;
    window.location.href = subscribeResponse.data.subscribeViaWeb;
  } catch (err) {
    errorMessage.value = getMessageFromError(err);
  }
  saving.value = false;
};
</script>

<template>
  <DefaultLayout>
    <article class="container framework-slot">
      <UserArea title="Nova assinatura">
        <form @submit.prevent="submit">
          <div
            v-if="errorMessage || cordovaStore.subscriptionError"
            class="alert alert-danger"
          >
            {{
              errorMessage ||
              getMessageFromError(cordovaStore.subscriptionError)
            }}
          </div>
          <div v-if="platform !== 'web'">
            <div class="row">
              <div class="col-12 col-md-6">
                <div
                  class="cordova-subscription"
                  v-for="(
                    cordovaSubscription, index
                  ) of cordovaStore.subscriptions"
                  :key="`plan-${cordovaSubscription.id}`"
                >
                  <h3>{{ cordovaSubscription.title }}</h3>
                  <p v-if="cordovaSubscription.description">
                    {{ cordovaSubscription.description }}
                  </p>
                  <p>
                    Valor: {{ cordovaSubscription.price }}<br />
                    <template v-if="cordovaSubscription.expiryDate">
                      Validade até
                      {{ dateToUserWithTime(cordovaSubscription.expiryDate) }}
                    </template>
                  </p>
                  <button
                    v-if="cordovaSubscription.canPurchase"
                    class="btn btn-primary"
                    @click.prevent="cordovaStore.subscribe(index)"
                  >
                    Assinar
                  </button>
                  <button
                    v-else-if="cordovaSubscription.state === 'approved'"
                    class="btn btn-primary"
                  >
                    Processando...
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div v-else-if="userLoading || subscriptionsLoading" class="row">
            <div class="col-md-12 mb-3">CARREGANDO DADOS...</div>
          </div>
          <div v-else-if="userError || subscriptionsError" class="row">
            <div class="col-md-12 mb-3">
              {{ getMessageFromError(userError || subscriptionsError) }}
            </div>
          </div>
          <div v-else-if="invoiceUrl">
            <p>
              Clique no link abaixo para efetuar o pagamento: <br />
              <a :href="invoiceUrl" target="_blank">{{ invoiceUrl }}</a>
            </p>
          </div>
          <template
            v-else-if="
              subscriptions && subscriptions.length && subscriptions[0].active
            "
          >
            <p>Você já possui uma assinatura ativa</p>
          </template>
          <template v-else-if="trialElegible">
            <NewTrial
              :user="user"
              :subscriptions="subscriptions"
              @trialActivated="onTrialActivated"
            />
          </template>
          <template v-else>
            <legend>Plano</legend>
            <fieldset>
              <div class="row">
                <div class="col-12">
                  <div class="form-check form-check mb-1">
                    <input
                      class="form-check-input"
                      type="radio"
                      name="plan"
                      id="plan-MONTHLY"
                      value="MONTHLY"
                      v-model="plan"
                    />
                    <label class="form-check-label" for="plan-MONTHLY">
                      Mensal - {{ formatCurrency(monthlyValue) }} por mês
                    </label>
                  </div>
                  <div class="form-check form-check mb-3 d-none">
                    <input
                      class="form-check-input"
                      type="radio"
                      name="plan"
                      id="plan-YEARLY"
                      value="YEARLY"
                      v-model="plan"
                    />
                    <label class="form-check-label" for="plan-YEARLY">
                      Anual - {{ formatCurrency(yearlyValue) }}
                      <template
                        v-if="
                          installmentCount > 1 && billingType === 'CREDIT_CARD'
                        "
                      >
                        ({{ installmentCount }}x
                        {{ formatCurrency(yearlyValue / installmentCount) }})
                      </template>
                      por ano
                    </label>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-12">
                  <div
                    class="btn-group mb-3 btn-group-billingType"
                    role="group"
                  >
                    <input
                      type="radio"
                      class="btn-check"
                      name="billingType"
                      id="billing-type-CREDIT_CARD"
                      autocomplete="off"
                      value="CREDIT_CARD"
                      v-model="billingType"
                    />
                    <label
                      class="btn btn-secondary"
                      for="billing-type-CREDIT_CARD"
                      >Cartão de crédito</label
                    >

                    <input
                      type="radio"
                      class="btn-check"
                      name="billingType"
                      id="billing-type-PIX"
                      autocomplete="off"
                      value="PIX"
                      v-model="billingType"
                    />
                    <label class="btn btn-secondary" for="billing-type-PIX"
                      >PIX</label
                    >

                    <input
                      type="radio"
                      class="btn-check"
                      name="billingType"
                      id="billing-type-BOLETO"
                      autocomplete="off"
                      value="BOLETO"
                      v-model="billingType"
                    />
                    <label class="btn btn-secondary" for="billing-type-BOLETO"
                      >Boleto</label
                    >
                  </div>
                </div>
                <FormGroup
                  v-if="plan === 'YEARLY' && billingType === 'CREDIT_CARD'"
                  class="col-12"
                  label="Número de parcelas"
                  labelFor="installment-count"
                >
                  <select
                    id="installment-count"
                    class="form-select"
                    v-model="installmentCount"
                  >
                    <option
                      v-for="(optionStr, count) in installmentOptions"
                      :key="`listallmentCount-${count}`"
                      :value="parseInt(count.toString())"
                    >
                      {{ optionStr }}
                    </option>
                  </select>
                </FormGroup>
              </div>
            </fieldset>
            <legend>Informações fiscais</legend>
            <fieldset>
              <div class="row">
                <div class="col-12">
                  <div class="form-check form-check-inline mb-3">
                    <input
                      class="form-check-input"
                      type="radio"
                      name="kind-of-person"
                      id="kind-of-person-NATURAL_PERSON"
                      value="NATURAL_PERSON"
                      v-model="kindOfPerson"
                      required
                    />
                    <label
                      class="form-check-label"
                      for="kind-of-person-NATURAL_PERSON"
                      >Pessoa física</label
                    >
                  </div>
                  <div class="form-check form-check-inline mb-3">
                    <input
                      class="form-check-input"
                      type="radio"
                      name="kind-of-person"
                      id="kind-of-person-LEGAL_PERSON"
                      value="LEGAL_PERSON"
                      v-model="kindOfPerson"
                      required
                    />
                    <label
                      class="form-check-label"
                      for="kind-of-person-LEGAL_PERSON"
                      >Pessoa jurídica</label
                    >
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label for="legal-name" class="form-label">
                      {{
                        kindOfPerson === "NATURAL_PERSON"
                          ? "Nome completo"
                          : "Razão Social"
                      }}
                    </label>
                    <div class="input-group">
                      <input
                        id="legal-name"
                        type="text"
                        class="form-control"
                        v-model="legalName"
                        required
                      />
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label for="reg-number" class="form-label">
                      {{ kindOfPerson === "NATURAL_PERSON" ? "CPF" : "CNPJ" }}
                    </label>
                    <div class="input-group">
                      <input
                        id="reg-number"
                        v-maska="
                          kindOfPerson === 'NATURAL_PERSON'
                            ? '###.###.###-##'
                            : '##.###.###/####-##'
                        "
                        type="text"
                        class="form-control"
                        v-model="regNumber"
                        required
                      />
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label for="address-line-1" class="form-label"
                      >Endereço</label
                    >
                    <div class="input-group">
                      <input
                        id="address-line-1"
                        type="text"
                        class="form-control"
                        autocomplete="address-line1"
                        v-model="addressLine1"
                        required
                      />
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="form-group mb-3">
                    <label for="address-number" class="form-label"
                      >Número</label
                    >
                    <div class="input-group">
                      <input
                        id="address-number"
                        type="text"
                        class="form-control"
                        v-model="addressNumber"
                        required
                      />
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="form-group mb-3">
                    <label for="address-line-2" class="form-label"
                      >Complemento</label
                    >
                    <div class="input-group">
                      <input
                        id="address-line-2"
                        type="text"
                        class="form-control"
                        autocomplete="address-line2"
                        v-model="addressLine2"
                      />
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label for="address-line-3" class="form-label"
                      >Bairro</label
                    >
                    <div class="input-group">
                      <input
                        id="address-line-3"
                        type="text"
                        class="form-control"
                        autocomplete="address-level3"
                        v-model="addressLine3"
                        required
                      />
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label for="city" class="form-label">Cidade</label>
                    <div class="input-group">
                      <input
                        id="city"
                        type="text"
                        class="form-control"
                        v-model="city"
                        required
                      />
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label for="state" class="form-label">Estado</label>
                    <div class="input-group">
                      <select
                        id="state"
                        class="form-select"
                        v-model="state"
                        required
                      >
                        <option value="AC">Acre</option>
                        <option value="AL">Alagoas</option>
                        <option value="AP">Amapá</option>
                        <option value="AM">Amazonas</option>
                        <option value="BA">Bahia</option>
                        <option value="CE">Ceará</option>
                        <option value="DF">Distrito Federal</option>
                        <option value="ES">Espírito Santo</option>
                        <option value="GO">Goiás</option>
                        <option value="MA">Maranhão</option>
                        <option value="MT">Mato Grosso</option>
                        <option value="MS">Mato Grosso do Sul</option>
                        <option value="MG">Minas Gerais</option>
                        <option value="PA">Pará</option>
                        <option value="PB">Paraíba</option>
                        <option value="PR">Paraná</option>
                        <option value="PE">Pernambuco</option>
                        <option value="PI">Piauí</option>
                        <option value="RJ">Rio de Janeiro</option>
                        <option value="RN">Rio Grande do Norte</option>
                        <option value="RS">Rio Grande do Sul</option>
                        <option value="RO">Rondônia</option>
                        <option value="RR">Roraima</option>
                        <option value="SC">Santa Catarina</option>
                        <option value="SP">São Paulo</option>
                        <option value="SE">Sergipe</option>
                        <option value="TO">Tocantins</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label for="postal-code" class="form-label">CEP</label>
                    <div class="input-group">
                      <input
                        id="postal-code"
                        type="text"
                        class="form-control"
                        v-maska="'#####-###'"
                        v-model="postalCode"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
            </fieldset>
            <div class="row">
              <div class="col">
                <button
                  v-if="saving"
                  class="btn btn-submit btn-primary mb-3"
                  type="submit"
                  disabled
                >
                  Salvando...
                </button>
                <button
                  v-else
                  class="btn btn-submit btn-primary mb-3"
                  type="submit"
                >
                  Assinar
                </button>
              </div>
            </div>
          </template>
        </form>
      </UserArea>
    </article>
  </DefaultLayout>
</template>

<style scoped>
.btn-group-billingType {
  width: 100%;
}

.btn-group-billingType .btn {
  width: 33%;
}

.cordova-subscription {
  background: var(--white-ice);
  padding: 10px;
  border-radius: 0.25rem;
  margin-bottom: 10px;
  color: var(--dark-grey);
}

.cordova-subscription h3 {
  font-size: 1.2rem;
  color: var(--black-eigengrau);
  margin-bottom: 0.5rem;
}

.cordova-subscription p {
  margin-bottom: 0.5rem;
}
</style>
